/**
 * Scope-prefixed TanStack Query keys.
 *
 * Every cache entry is prefixed with the org (and site where physical) so that
 * switching scope in the ScopeBar isolates caches cleanly and a targeted
 * invalidation never bleeds across tenants. Shape convention:
 *
 *   ["v2", "<org|_>", "<site|_>", "<resource>", ...params]
 *
 * "_" is the placeholder for "no scope selected yet" / "all" so keys stay
 * fixed-arity and comparable.
 */

import type { DeviceListParams, ListParams } from "./types";

/** Normalize an optional scope id into a stable key segment. */
const seg = (v: string | null | undefined): string => v ?? "_";

export const qk = {
  /** Identity is global (not scoped) — it defines the scopes. */
  me: () => ["v2", "me"] as const,

  /** Global search (command palette). Cross-org by design — keyed on the term only. */
  search: (q: string) => ["v2", "search", q] as const,

  devices: {
    /** Keyed on the search/kind/scope params so changing the search term or the equipment
     *  type refetches instead of re-rendering the previous term's rows under the new one. */
    list: (orgId: string | null, siteId: string | null, params?: DeviceListParams) =>
      ["v2", seg(orgId), seg(siteId), "devices", params ?? {}] as const,
    /** The onboarding pool: org devices not yet placed at a site. */
    unassigned: (orgId: string | null) =>
      ["v2", seg(orgId), "devices", "unassigned"] as const,
    /** Devices addable to a site (unassigned + placed elsewhere). */
    assignable: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "assignable"] as const,
    /** Fleet-wide status bucket counts (the list's four clickable tiles). Keyed on the
     *  same filters the request carries, so switching customer/site/group/kind refetches
     *  rather than showing the previous page's numbers under the new heading. */
    statusCounts: (orgId: string | null, siteId: string | null,
                   filters?: Record<string, string | undefined>) =>
      ["v2", seg(orgId), seg(siteId), "devices", "status-counts", filters ?? {}] as const,
    /** Fleet-wide latest config-verification verdict (the list's config-status column). */
    configStatus: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), seg(siteId), "devices", "config-status"] as const,
    /** Fleet-wide hourly connectivity buckets (the list's sparkline column). */
    connectivity: (orgId: string | null, siteId: string | null, hours: number) =>
      ["v2", seg(orgId), seg(siteId), "devices", "connectivity", hours] as const,
    /** One device's connectivity timeline (the device-page chart). */
    deviceConnectivity: (orgId: string | null, serial: string, hours: number) =>
      ["v2", seg(orgId), "devices", serial, "connectivity", hours] as const,
    /** One device's applied-state verification (verdict + history). */
    verification: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "verification"] as const,
    /** One device's field-level config drift (OP#1783). Under "verification" so anything
     *  that invalidates the verdict (exclude/include, a re-check) refreshes the diff too. */
    drift: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "verification", "drift"] as const,
    /** An AP's tags/groups. */
    tags: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "tags"] as const,
    /** An AP's per-band radio settings: effective value + source, overrides (OP#1760). */
    radios: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "radios"] as const,
    /** A device's installer photos (the sheet's "Location & photos" strip). */
    photos: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "photos"] as const,
    /** Live SNMP port view for a switch. */
    switchPorts: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "switch-ports"] as const,
    /** Rich per-port switch detail (SNMP) for the switch detail screen. */
    switchDetail: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "switch-detail"] as const,
    /** Current VLANs per switch port (mode, untagged, tagged, STP, drift). */
    portVlans: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "port-vlans"] as const,
    /** Per-kind device rollup for the org/site "Overview" cards. */
    summary: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), seg(siteId), "device-summary"] as const,
    /** Config-action catalog for a device (the "Configuration" panel). */
    configActions: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "config-actions"] as const,
    ports: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "ports"] as const,
    /** The AP PORT PLANE: every physical port with its effective setting and the layer that
     *  setting came from (override / profile / device). Separate from `ports` so an apply can
     *  invalidate the editor without re-fetching the whole device sheet. */
    apPorts: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "ap-ports"] as const,
    /** LIVE per-port view (link, VLANs, clients/MACs, LLDP, traffic) for APs and switches. */
    livePorts: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "ports-live"] as const,
    /** The SSIDs a single AP is broadcasting (live config + client counts). */
    ssids: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "ssids"] as const,
    /** Read-only gateway view (interfaces/VLANs/DHCP/NAT) of a uCentral gateway. */
    gateway: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "gateway"] as const,
    /** One AP's baseline status (factory routed LAN / 6 GHz on open SSIDs), device card. */
    apBaseline: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "ap-baseline"] as const,
    /** SonicFi/CyberTan RSW adopt preview: config of record built from the running switch (OP#1838). */
    sonicfiAdopt: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "sonicfi-adopt"] as const,
    /** The EDITABLE gateway LAN/VLAN table (Gateway > VLANs). Separate from `gateway` so an
     *  apply can invalidate the editor without re-fetching the whole device sheet. */
    gatewayNetworks: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "gateway-networks"] as const,
    /** The Gateway > DHCP page (scopes, fixed IPs, reported lease evidence). */
    gatewayDhcp: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "gateway-dhcp"] as const,
    /** One gateway's firewall posture (port forwards, isolation, blocked destinations). */
    gatewayFirewall: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "gateway", "firewall"] as const,
    /** Firmware panel: current version, latest available image, latest update status. */
    firmware: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "devices", serial, "firmware"] as const,
    detail: (orgId: string | null, serial: string) =>
      ["v2", seg(orgId), "_", "device", serial] as const,
  },

  clients: {
    list: (orgId: string | null, siteId: string | null, params?: ListParams) =>
      ["v2", seg(orgId), seg(siteId), "clients", params ?? {}] as const,
    /** "Where is this MAC?" — cross-site within the org, so keyed on org + MAC only. */
    location: (orgId: string | null, mac: string) =>
      ["v2", seg(orgId), "_", "client-location", mac] as const,
  },

  /** MSP portal: the customers (orgs) landing feed. */
  customers: () => ["v2", "customers"] as const,
  /** Customer Overview: one row per visible site, for the header-selected customer. */
  sitesOverview: (orgId: string | null) => ["v2", seg(orgId), "sites-overview"] as const,
  /** RADIUS Servers registry (org-scoped, BYO). */
  radiusServers: (orgId: string | null) => ["v2", seg(orgId), "radius-servers"] as const,

  /** The people who can log in, for the current customer scope. */
  operators: (orgId: string | null) => ["v2", seg(orgId), "operators"] as const,
  /** Platform staff (platform_admin only): org-less GTK staff + test accounts. Not per customer. */
  platformStaff: () => ["v2", "platform-staff"] as const,
  /** Whether operator management is usable at all (IdP wired + attributes declared). */
  operatorPreflight: (orgId: string | null) =>
    ["v2", seg(orgId), "operators", "preflight"] as const,

  /**
   * The people who CONNECT (Organization > Users), for the current customer scope.
   *
   * Keyed by the search term as well as the org: the list is server-filtered, so two
   * different searches are two different results and must not share a cache entry.
   */
  networkUsers: (orgId: string | null, q?: string) =>
    ["v2", seg(orgId), "network-users", q ?? ""] as const,
  /** Managed RADIUS realms (org-scoped, the Passpoint keystone). */
  radiusRealms: (orgId: string | null) => ["v2", seg(orgId), "radius-realms"] as const,
  /** Credentials within one managed-RADIUS realm. */
  radiusCredentials: (orgId: string | null, realmId: number | null) =>
    ["v2", seg(orgId), "radius-realms", seg(realmId == null ? null : String(realmId)),
      "credentials"] as const,
  sites: {
    /** Sites (networks) under an org. */
    list: (orgId: string | null) => ["v2", seg(orgId), "sites", "list"] as const,
    /** The site's SSIDs (Wireless > SSIDs). */
    ssids: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "ssids"] as const,
    /** The site's radios (Wireless > Radio Settings). */
    radios: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "radios"] as const,
    /** Top-talker bandwidth ranking for the scope (Network-wide > Usage). */
    clientUsage: (orgId: string | null, siteId: string | null, params?: Record<string, unknown>) =>
      ["v2", seg(orgId), "sites", seg(siteId), "clients", "usage", params ?? {}] as const,

    /** Firmware drift against the approved baseline per device type. */
    firmwareCompliance: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "firmware", "compliance"] as const,
    /** Live routing tables read from the site's gateways (Gateway > Route Table). */
    gatewayRoutes: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "gateway-routes"] as const,
    /** Aggregated security-posture findings for the site's gateways (Gateway > Security Posture). */
    gatewayPosture: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "gateway-posture"] as const,

    /** The site's captive/guest portals (Hospitality). */
    portals: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "portals"] as const,
    /** The site's Passpoint/Hotspot 2.0 state per SSID (Hospitality). */
    passpoint: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "passpoint"] as const,
    /** The site's guest vouchers (Hospitality). */
    vouchers: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "vouchers"] as const,
    /** Multi-PSK / IPSK keys on one SSID (Wireless). */
    psks: (orgId: string | null, siteId: string | null, ssid: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "ssids", seg(ssid), "psks"] as const,
    /** The site's tags/groups. */
    tags: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "tags"] as const,
    /** The site's device pre-registrations (MAC/serial -> auto-claim on arrival). */
    preregs: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "preregs"] as const,
    /** The site's named wired networks (label + VLAN) for AP port profiles. */
    wiredNetworks: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "wired-networks"] as const,
    /** The site's AP port profiles (per-port maps). */
    portProfiles: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "port-profiles"] as const,
    /** Site config-action catalog (the "Configure" page fan-out). */
    configActions: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "config-actions"] as const,
    /** The site's PMS integration settings (Hospitality > PMS Integration). */
    pms: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "pms"] as const,
    /** The site's WAN watchdog policy (gateway connectivity remediation). */
    wanWatchdog: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "wan-watchdog"] as const,
    /** The site's VLAN list (Site > Configure > VLANs). */
    siteVlans: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "vlans"] as const,
    /** The READ-ONLY rollout plan for every gateway and switch at the site. */
    vlanRolloutPlan: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "vlan-rollout", "plan"] as const,
    /** The latest rollout run (per-device progress + verification). */
    vlanRolloutStatus: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "vlan-rollout", "status"] as const,
    /** Switches skipped while offline whose VLAN changes are still pending (site list). */
    vlanPendingSwitches: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "vlan-rollout", "pending-switches"] as const,
    /** One switch's pending VLAN changes (device sheet). */
    devicePendingVlans: (orgId: string | null, serial: string | null) =>
      ["v2", seg(orgId), "devices", seg(serial), "vlan-rollout", "pending"] as const,
    /** The latest site "Re-apply managed settings" run (from the audit log). */
    gatewayReassertStatus: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "gateways", "reassert", "status"] as const,
    /** GET /sites/{id}/vlan-auto-deploy -- auto-deploy state, last run, pending removals. */
    vlanAutoDeploy: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "vlan-auto-deploy"] as const,
    /** Site switch settings (AP management native VLAN). */
    switchSettings: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "switch-settings"] as const,
    /** The site's gateway VRRP pair. */
    gatewayHaPair: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "gateway-ha-pair"] as const,
    /** Site multicast intent (per-VLAN IGMP snooping) -- Switch > Multicast. */
    multicast: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "multicast"] as const,
    /** Live IGMP-snooping state read off the site's switches (what the switches see). */
    multicastState: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "multicast", "state"] as const,
    /** READ-ONLY multicast plan (exact ecCLI per switch + plan_token). */
    multicastPlan: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "multicast", "plan"] as const,
    /** Site Wi-Fi mesh: the mesh, live per-AP status, create-form options (Wireless > Mesh). */
    mesh: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "mesh"] as const,
    /** Read-only AP-baseline roll-up (which APs still need "Re-apply AP baseline"). */
    apBaseline: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), "sites", seg(siteId), "ap-baseline"] as const,
  },

  /** Assistant confirm-gated actions (Phase 2): AiProposals, org-scoped.
   *  `proposals` is the LIST-PREFIX — an invalidation of it clears every filtered list under the org
   *  (the list hook appends its ?kind/?status params as a further segment, so this stays its prefix). */
  assistant: {
    proposals: (orgId: string | null) => ["v2", seg(orgId), "assistant", "proposals"] as const,
    proposal: (orgId: string | null, id: number) =>
      ["v2", seg(orgId), "assistant", "proposals", id] as const,
  },

  /** LLDP-derived topology graph (Network-wide > Topology). */
  topology: (orgId: string | null, siteId: string | null) =>
    ["v2", seg(orgId), seg(siteId), "topology"] as const,
  /** Live per-AP radio state (Wireless > RF Spectrum). */
  rf: (orgId: string | null, siteId: string | null) =>
    ["v2", seg(orgId), seg(siteId), "rf"] as const,
  /** Firmware inventory + rollout status (Network > Firmware). */
  firmware: {
    images: (orgId: string | null) => ["v2", seg(orgId), "firmware", "images"] as const,
    updates: (orgId: string | null) => ["v2", seg(orgId), "firmware", "updates"] as const,
  },
  health: {
    /** The dashboard worklist — the "needs attention" feed. */
    worklist: (
      orgId: string | null, siteId: string | null,
      severity?: string | null, kind?: string | null, limit?: number,
    ) =>
      // Every filter that changes the response is part of the key (OP#1854), so the Alerts
      // page never serves the Health page's shorter cached list as its own.
      ["v2", seg(orgId), seg(siteId), "health", "worklist", severity ?? "", kind ?? "", limit ?? 0] as const,
    summary: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), seg(siteId), "health", "summary"] as const,
  },

  dashboard: {
    /** The realtime aggregate overview (counts, clients, alerts, throughput series). */
    overview: (orgId: string | null, siteId: string | null) =>
      ["v2", seg(orgId), seg(siteId), "dashboard", "overview"] as const,
  },

  notifications: {
    /** Org-scoped alert-delivery channels. */
    channels: (orgId: string | null) => ["v2", seg(orgId), "_", "notifications", "channels"] as const,
  },

  catalog: {
    list: (orgId: string | null, kind: string, params?: ListParams) =>
      ["v2", seg(orgId), "_", "catalog", kind, params ?? {}] as const,
  },

  templates: {
    list: (orgId: string | null, params?: ListParams) =>
      ["v2", seg(orgId), "_", "templates", params ?? {}] as const,
  },

  audit: {
    list: (orgId: string | null, siteId: string | null, params?: ListParams) =>
      ["v2", seg(orgId), seg(siteId), "audit", params ?? {}] as const,
  },
};

/** Invalidate everything under one org (use after a scope-wide mutation). */
export const orgScopeKey = (orgId: string | null) => ["v2", seg(orgId)] as const;
